// src/components/Charts/NdviTimeline.jsx
import React, { useEffect, useState, useCallback } from 'react';
import {
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  Area,
  ComposedChart,
} from 'recharts';
import { fieldService } from '../../services/api';
import { getNdviColor } from '../../utils/ndviColors';
import { useLanguage } from '../../contexts/LanguageContext';
import './NdviTimeline.css';

const NdviTimeline = ({ fieldId, data, onPointClick, activeIndex = 'NDVI' }) => {
  const { t, language } = useLanguage();
  const currentLocale = language === 'en' ? 'en-US' : 'tr-TR';
  const [timelineData, setTimelineData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const loadTimeline = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await fieldService.getNdviTimeline(fieldId);

      const formattedData = data.map(item => ({
        date: new Date(item.acquisitionDate).toLocaleDateString(currentLocale, {
          day: '2-digit',
          month: 'short'
        }),
        fullDate: new Date(item.acquisitionDate).toLocaleDateString(currentLocale),
        average: activeIndex === 'NDVI' ? parseFloat(item.meanNdvi?.toFixed(3)) || 0 :
          activeIndex === 'NDRE' ? parseFloat(item.meanNdre?.toFixed(3)) || 0 :
            parseFloat(item.meanNdmi?.toFixed(3)) || 0,
        min: activeIndex === 'NDVI' ? parseFloat(item.minNdvi?.toFixed(3)) || 0 :
          activeIndex === 'NDRE' ? parseFloat(item.minNdre?.toFixed(3)) || 0 :
            parseFloat(item.minNdmi?.toFixed(3)) || 0,
        max: activeIndex === 'NDVI' ? parseFloat(item.maxNdvi?.toFixed(3)) || 0 :
          activeIndex === 'NDRE' ? parseFloat(item.maxNdre?.toFixed(3)) || 0 :
            parseFloat(item.maxNdmi?.toFixed(3)) || 0,
        bulutluluk: parseFloat((item.cloudCover * 100).toFixed(1)) || 0,
      }));

      setTimelineData(formattedData);
    } catch (err) {
      console.error('Timeline yüklenemedi:', err);
      // Mock data yoksa hata göster, varsa sessiz kal (UI'da gösterilir)
      setError('NDVI zaman serisi yüklenirken hata oluştu');
    } finally {
      setLoading(false);
    }
  }, [fieldId, activeIndex, currentLocale]);

  useEffect(() => {
    if (data) {
      // Dışarıdan veri geldiyse onu formatla
      const formatted = data.map(item => ({
        date: new Date(item.date).toLocaleDateString(currentLocale, {
          day: '2-digit',
          month: 'short'
        }),
        fullDate: new Date(item.date).toLocaleDateString(currentLocale),
        average: activeIndex === 'NDVI' ? parseFloat(item.meanNdvi?.toFixed(3)) || 0 :
          activeIndex === 'NDRE' ? parseFloat(item.meanNdre?.toFixed(3)) || 0 :
            parseFloat(item.meanNdmi?.toFixed(3)) || 0,
        min: activeIndex === 'NDVI' ? parseFloat(item.minNdvi?.toFixed(3)) || 0 :
          activeIndex === 'NDRE' ? parseFloat(item.minNdre?.toFixed(3)) || 0 :
            parseFloat(item.minNdmi?.toFixed(3)) || 0,
        max: activeIndex === 'NDVI' ? parseFloat(item.maxNdvi?.toFixed(3)) || 0 :
          activeIndex === 'NDRE' ? parseFloat(item.maxNdre?.toFixed(3)) || 0 :
            parseFloat(item.maxNdmi?.toFixed(3)) || 0,
        bulutluluk: parseFloat((item.cloudCover * 100).toFixed(1)) || 0,
        ...item // Orijinal veriyi de sakla (includes the date property)
      }));
      setTimelineData(formatted);
      setLoading(false);
    } else {
      loadTimeline();
    }
  }, [fieldId, data, activeIndex, loadTimeline, currentLocale]);

  const handleChartClick = (state) => {
    if (onPointClick && state && state.activePayload) {
      onPointClick(state.activePayload[0].payload);
    }
  };

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <div className="custom-tooltip">
          <p className="tooltip-date">{data.fullDate}</p>
          <p className="tooltip-value">
            <span style={{ color: '#2ecc71' }}>●</span> {t('timeline.average')} {activeIndex}: <strong>{data.average}</strong>
          </p>
          <p className="tooltip-value">
            <span style={{ color: '#3498db' }}>●</span> {t('timeline.min')}: {data.min}
          </p>
          <p className="tooltip-value">
            <span style={{ color: '#e74c3c' }}>●</span> {t('timeline.max')}: {data.max}
          </p>
          <p className="tooltip-value">
            <span style={{ color: '#95a5a6' }}>●</span> {t('timeline.cloud_cover')}: %{data.bulutluluk}
          </p>
        </div>
      );
    }
    return null;
  };

  if (loading) {
    return (
      <div className="ndvi-timeline-loading">
        <div className="spinner-small"></div>
        <p>{t('timeline.loading')}</p>
      </div>
    );
  }

  if (error && !data) {
    return (
      <div className="ndvi-timeline-error">
        <p>⚠️ {t('timeline.error')}</p>
        <button onClick={loadTimeline} className="retry-btn">
          🔄 {t('timeline.retry')}
        </button>
      </div>
    );
  }

  if (timelineData.length === 0) {
    return (
      <div className="ndvi-timeline-empty">
        <p>📊 {t('timeline.no_analysis_yet', { index: activeIndex })}</p>
        <p>{t('timeline.run_analysis_first')}</p>
      </div>
    );
  }

  return (
    <div className="ndvi-timeline">
      <div className="timeline-header">
        <h3>📈 {activeIndex} {t('timeline.time_series')}</h3>
        <div className="timeline-stats">
          <div className="stat-badge">
            <span className="stat-label">{t('timeline.analysis_count')}:</span>
            <span className="stat-value">{timelineData.length}</span>
          </div>
          <div className="stat-badge">
            <span className="stat-label">{t('timeline.last_value')} {activeIndex}:</span>
            <span className="stat-value" style={{
              color: activeIndex === 'NDVI' ? getNdviColor(timelineData[timelineData.length - 1]?.average || 0) :
                activeIndex === 'NDMI' ? '#228B22' : '#2c7a7b'
            }}>
              {timelineData[timelineData.length - 1]?.average || '-'}
            </span>
          </div>
        </div>
      </div>

      <ResponsiveContainer width="100%" height={400}>
        <ComposedChart
          data={timelineData}
          onClick={handleChartClick}
          style={{ cursor: 'pointer' }}
        >
          <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
          <XAxis
            dataKey="date"
            angle={-45}
            textAnchor="end"
            height={80}
            tick={{ fontSize: 12 }}
          />
          <YAxis
            domain={[-0.1, 1]}
            label={{
              value: activeIndex,
              angle: -90,
              position: 'insideLeft',
              style: { fontSize: 14, fontWeight: 'bold' }
            }}
            tick={{ fontSize: 12 }}
          />
          <Tooltip content={<CustomTooltip />} />
          <Legend
            wrapperStyle={{ paddingTop: '20px' }}
            iconType="line"
          />

          <Area
            type="monotone"
            dataKey="max"
            stackId="1"
            stroke="none"
            fill="#e3f2fd"
            fillOpacity={0.6}
            name={`Max ${activeIndex}`}
          />
          <Area
            type="monotone"
            dataKey="min"
            stackId="1"
            stroke="none"
            fill="#ffffff"
            fillOpacity={1}
            name={`Min ${activeIndex}`}
          />

          <Line
            type="monotone"
            dataKey="average"
            stroke={activeIndex === 'NDVI' ? '#2ecc71' : activeIndex === 'NDMI' ? '#228B22' : '#2c7a7b'}
            strokeWidth={3}
            dot={{ r: 5, fill: activeIndex === 'NDVI' ? '#2ecc71' : activeIndex === 'NDMI' ? '#228B22' : '#2c7a7b' }}
            activeDot={{ r: 8, onClick: (e, payload) => onPointClick && onPointClick(payload.payload) }}
            name={`Average ${activeIndex}`}
          />
        </ComposedChart>
      </ResponsiveContainer>

      {/* Değer Açıklaması */}
      <div className="ndvi-legend">
        {activeIndex === 'NDMI' ? (
          <>
            <h4>{t('ndmiLegend.title')}</h4>
            <div className="legend-grid" style={{ gridTemplateColumns: 'repeat(3, 1fr)' }}>
              <div className="legend-item">
                <span className="color-box" style={{ background: '#8B4513' }}></span>
                <span>-0.5 : Kritik</span>
              </div>
              <div className="legend-item">
                <span className="color-box" style={{ background: '#CD5C5C' }}></span>
                <span>-0.3 : {t('ndmiLegend.highStress')}</span>
              </div>
              <div className="legend-item">
                <span className="color-box" style={{ background: '#FF8C00' }}></span>
                <span>-0.1 : {t('ndmiLegend.mediumStress')}</span>
              </div>
              <div className="legend-item">
                <span className="color-box" style={{ background: '#FFD700' }}></span>
                <span>0.0 : {t('ndmiLegend.attention')}</span>
              </div>
              <div className="legend-item">
                <span className="color-box" style={{ background: '#F0E68C' }}></span>
                <span>0.1 : {t('ndmiLegend.lightStress')}</span>
              </div>
              <div className="legend-item">
                <span className="color-box" style={{ background: '#87CEEB' }}></span>
                <span>0.2 : {t('ndmiLegend.normal')}</span>
              </div>
              <div className="legend-item">
                <span className="color-box" style={{ background: '#4169E1' }}></span>
                <span>0.3 : {t('ndmiLegend.good')}</span>
              </div>
              <div className="legend-item">
                <span className="color-box" style={{ background: '#0000CD' }}></span>
                <span>0.4 : {t('ndmiLegend.veryGood')}</span>
              </div>
              <div className="legend-item">
                <span className="color-box" style={{ background: '#00008B' }}></span>
                <span>0.5+ : {t('ndmiLegend.moist')}</span>
              </div>
            </div>
          </>
        ) : (
          <>
            <h4>{t('ndviLegend.title')}</h4>
            <div className="legend-grid">
              <div className="legend-item">
                <span className="color-box" style={{ background: '#8B4513' }}></span>
                <span>&lt; 0.2: {t('ndviLegend.bareSoil')}</span>
              </div>
              <div className="legend-item">
                <span className="color-box" style={{ background: '#FFD700' }}></span>
                <span>0.2 - 0.4: {t('ndviLegend.weakVegetation')}</span>
              </div>
              <div className="legend-item">
                <span className="color-box" style={{ background: '#FFFF00' }}></span>
                <span>0.4 - 0.6: {t('ndviLegend.mediumVegetation')}</span>
              </div>
              <div className="legend-item">
                <span className="color-box" style={{ background: '#7FFF00' }}></span>
                <span>0.6 - 0.8: {t('ndviLegend.healthyVegetation')}</span>
              </div>
              <div className="legend-item">
                <span className="color-box" style={{ background: '#006400' }}></span>
                <span>&gt; 0.8: {t('ndviLegend.veryHealthyVegetation')}</span>
              </div>
            </div>
          </>
        )}
      </div>

      {timelineData.length >= 2 && (
        <div className="trend-analysis">
          <h4>📊 {t('trendAnalysis.title')}</h4>
          <div className="trend-content">
            {(() => {
              const first = timelineData[0].average;
              const last = timelineData[timelineData.length - 1].average;
              const change = last - first;
              const changePercent = first !== 0 ? ((change / first) * 100).toFixed(1) : 0;

              return (
                <>
                  <div className="trend-item">
                    <span className="trend-label">{t('trendAnalysis.firstMeasurement')}:</span>
                    <span className="trend-value">{first}</span>
                  </div>
                  <div className="trend-item">
                    <span className="trend-label">{t('trendAnalysis.lastMeasurement')}:</span>
                    <span className="trend-value">{last}</span>
                  </div>
                  <div className="trend-item">
                    <span className="trend-label">{t('trendAnalysis.change')}:</span>
                    <span className={`trend-value ${change >= 0 ? 'positive' : 'negative'}`}>
                      {change >= 0 ? '↗' : '↘'} {Math.abs(change).toFixed(3)} ({changePercent}%)
                    </span>
                  </div>
                  <div className="trend-description">
                    {change >= 0.1 ? (
                      <span className="trend-good">✅ {t('trendAnalysis.significantImprovement')}</span>
                    ) : change >= 0 ? (
                      <span className="trend-stable">➡️ {t('trendAnalysis.stableHealth')}</span>
                    ) : change >= -0.1 ? (
                      <span className="trend-warning">⚠️ {t('trendAnalysis.slightDecline')}</span>
                    ) : (
                      <span className="trend-bad">❌ {t('trendAnalysis.significantDecline')}</span>
                    )}
                  </div>
                </>
              );
            })()}
          </div>
        </div>
      )}
    </div>
  );
};

export default NdviTimeline;